import React, { useEffect, useState } from "react";
import {
  Drawer, Box, Typography, TextField, Button, Divider, IconButton,
  Chip, Autocomplete, alpha, useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { KnowledgehubFolderResponse, KnowledgehubFileResponse } from "../../../../app/models/responseModels/knowledgehubResponse";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../app/features/store/store";
import { getFdpRoles, getFdcRoles, getOrganizationEmails } from "../../../../app/features/knowledgehub/knowledgehubSlice";
import { toast } from "react-toastify";

type EditTarget =
  | { type: "folder"; data: KnowledgehubFolderResponse }
  | { type: "file"; data: KnowledgehubFileResponse };

type Props = {
  target: EditTarget | null;
  onClose: () => void;
  onSave: (target: EditTarget, updated: { name?: string; title?: string; description?: string; fdpUsers?: number[]; fdcUsers?: number[]; manualEmails?: string[] }) => void;
};

export default function KnowledgehubEditDrawer({ target, onClose, onSave }: Props) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { fdpRoles, fdcRoles, organizationEmails } = useSelector((state: RootState) => state.knowledgehub);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [fdpUsers, setFdpUsers] = useState<number[]>([]);
  const [fdcUsers, setFdcUsers] = useState<number[]>([]);
  const [manualEmails, setManualEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [editEmailDialog, setEditEmailDialog] = useState<{ open: boolean; oldEmail: string; newEmail: string }>({ open: false, oldEmail: "", newEmail: "" });

  useEffect(() => {
    if (target) {
      dispatch(getFdpRoles());
      dispatch(getFdcRoles());
      dispatch(getOrganizationEmails());
    }
  }, [target, dispatch]);

  useEffect(() => {
    if (!target) return;

    if (target.type === "folder") {
      setName(target.data.name);
      setDescription(target.data.description ?? "");
      const fdpIds = target.data.fdpUsers?.map(u => u.id) ?? [];
      const fdcIds = target.data.fdcUsers?.map(u => u.id) ?? [];
      setFdpUsers(fdpIds);
      setFdcUsers(fdcIds);
      setManualEmails(target.data.emails ?? []);
    } else {
      setName(target.data.title);
      setDescription(target.data.description ?? "");
      const fdpIds = target.data.fdpUsers?.map(u => u.id) ?? [];
      const fdcIds = target.data.fdcUsers?.map(u => u.id) ?? [];
      setFdpUsers(fdpIds);
      setFdcUsers(fdcIds);
      setManualEmails(target.data.emails ?? []);
    }
  }, [target]);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const isEmailInOrganization = (email: string) => organizationEmails.includes(email.trim());

  const handleAddEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) { setEmailError("Invalid email"); return; }
    if (!isEmailInOrganization(trimmed)) { setEmailError("This email is not present in our organization"); return; }
    if (manualEmails.includes(trimmed)) { setEmailError("Already added"); return; }
    setManualEmails([...manualEmails, trimmed]);
    setEmailInput("");
    setEmailError("");
  };

  const handleEditEmail = (email: string) => {
    setEditEmailDialog({ open: true, oldEmail: email, newEmail: email });
  };

  const handleSaveEditEmail = () => {
    const trimmed = editEmailDialog.newEmail.trim();
    if (!trimmed || !isValidEmail(trimmed)) return;
    if (!isEmailInOrganization(trimmed)) {
      toast.error("This email is not present in our organization");
      return;
    }
    if (trimmed !== editEmailDialog.oldEmail && manualEmails.includes(trimmed)) return;
    setManualEmails(manualEmails.map((e) => e === editEmailDialog.oldEmail ? trimmed : e));
    setEditEmailDialog({ open: false, oldEmail: "", newEmail: "" });
  };

  const handleSave = () => {
    if (!target) return;
    const updated = target.type === "folder"
      ? {
          id: target.data.id,
          name,
          description,
          parentId: null,
          fdpRoleIds: fdpUsers,
          fdcRoleIds: fdcUsers,
          manualEmails,
          allUsers: false,
        }
      : {
          id: target.data.id,
          description,
          fdpRoleIds: fdpUsers,
          fdcRoleIds: fdcUsers,
          manualEmails,
          allUsers: false,
        };
    onSave(target, updated);
    onClose();
  };

  const isValid = target?.type === "folder" ? name.trim().length > 0 : true;
  const label = target?.type === "folder" ? "Repository Name" : "File Title";
  const title = target?.type === "folder" ? "Edit Repository" : "Edit File";

  return (
    <Drawer
      anchor="right"
      open={!!target}
      onClose={onClose}
      PaperProps={{ sx: { width: 400, p: 0 } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2 }}>
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Divider />

      <Box sx={{ px: 3, py: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        {target?.type === "folder" ? (
          <>
            {/* Folder: Name */}
            <TextField
              label={label}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              required
            />

            {/* Folder: Description */}
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={3}
            />

            {/* Folder: FDP Roles — commented out as requested */}
            {/* <Autocomplete
              multiple
              options={fdpRoles}
              getOptionLabel={(option) => option.name}
              value={fdpRoles.filter((r) => fdpUsers.includes(r.id))}
              onChange={(_, selected) => setFdpUsers(selected.map(r => r.id))}
              renderInput={(params) => <TextField {...params} label="FDP Roles" size="small" placeholder="Select FDP roles" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.name} size="small" {...getTagProps({ index })} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }} />
                ))
              }
            /> */}

            {/* Folder: FDC Roles */}
            {/* <Autocomplete
              multiple
              options={fdcRoles}
              getOptionLabel={(option) => option.name}
              value={fdcRoles.filter((r) => fdcUsers.includes(r.id))}
              onChange={(_, selected) => setFdcUsers(selected.map(r => r.id))}
              renderInput={(params) => <TextField {...params} label="FDC Roles" size="small" placeholder="Select FDC roles" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.name} size="small" {...getTagProps({ index })} sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }} />
                ))
              }
            /> */}

            {/* Folder: Email — commented out as requested */}
            {/* <Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  label="Add Email"
                  value={emailInput}
                  onChange={(e) => { setEmailInput(e.target.value); setEmailError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddEmail(); } }}
                  size="small"
                  fullWidth
                  error={!!emailError}
                  helperText={emailError || "Press Enter or click + to add"}
                />
                <IconButton onClick={handleAddEmail} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, alignSelf: "flex-start", mt: 0.5 }}>
                  <AddIcon />
                </IconButton>
              </Box>
              {manualEmails.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 1.5 }}>
                  {manualEmails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      size="small"
                      onDelete={() => setManualEmails(manualEmails.filter((m) => m !== email))}
                      onClick={() => handleEditEmail(email)}
                      icon={<EditIcon sx={{ fontSize: "0.9rem" }} />}
                      sx={{ bgcolor: alpha(theme.palette.success.main, 0.08), color: "text.primary", cursor: "pointer" }}
                    />
                  ))}
                </Box>
              )}
            </Box> */}
          </>
        ) : (
          <>
            {/* File: Description */}
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={3}
            />

            {/* File: FDP Roles */}
            <Autocomplete
              multiple
              options={fdpRoles}
              getOptionLabel={(option) => option.name}
              value={fdpRoles.filter((r) => fdpUsers.includes(r.id))}
              onChange={(_, selected) => setFdpUsers(selected.map(r => r.id))}
              renderInput={(params) => <TextField {...params} label="FDP Roles" size="small" placeholder="Select FDP roles" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.name} size="small" {...getTagProps({ index })} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }} />
                ))
              }
            />

            {/* File: FDC Roles */}
            <Autocomplete
              multiple
              options={fdcRoles}
              getOptionLabel={(option) => option.name}
              value={fdcRoles.filter((r) => fdcUsers.includes(r.id))}
              onChange={(_, selected) => setFdcUsers(selected.map(r => r.id))}
              renderInput={(params) => <TextField {...params} label="FDC Roles" size="small" placeholder="Select FDC roles" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.name} size="small" {...getTagProps({ index })} sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }} />
                ))
              }
            />

            {/* File: Email */}
            <Box>
              <Autocomplete
                freeSolo
                options={organizationEmails || []}
                value={emailInput}
                onInputChange={(_, newValue) => {
                  setEmailInput(newValue || "");
                  setEmailError("");
                }}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add Email"
                    size="small"
                    error={!!emailError}
                    helperText={emailError || "Press Enter or click + to add"}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddEmail(); } }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <IconButton
                            onClick={handleAddEmail}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              mr: 0.5,
                              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </>
                      ),
                    }}
                  />
                )}
              />
              {manualEmails.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 1.5 }}>
                  {manualEmails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      size="small"
                      onDelete={() => setManualEmails(manualEmails.filter((m) => m !== email))}
                      onClick={() => handleEditEmail(email)}
                      icon={<EditIcon sx={{ fontSize: "0.9rem" }} />}
                      sx={{ bgcolor: alpha(theme.palette.success.main, 0.08), color: "text.primary", cursor: "pointer" }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      <Divider />

      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small">Cancel</Button>
        <Button onClick={handleSave} variant="contained" size="small" disabled={!isValid}>Save</Button>
      </Box>

      {/* Edit Email Dialog */}
      <Drawer
        anchor="right"
        open={editEmailDialog.open}
        onClose={() => setEditEmailDialog({ open: false, oldEmail: "", newEmail: "" })}
        PaperProps={{ sx: { width: 350, p: 0 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight={600}>Edit Email</Typography>
          <IconButton onClick={() => setEditEmailDialog({ open: false, oldEmail: "", newEmail: "" })} size="small"><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <Divider />
        <Box sx={{ px: 3, py: 2.5 }}>
          <Autocomplete
            freeSolo
            options={organizationEmails || []}
            value={editEmailDialog.newEmail}
            onInputChange={(_, newValue) => {
              setEditEmailDialog((prev) => ({ ...prev, newEmail: newValue || "" }));
            }}
            getOptionLabel={(option) => option}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email Address"
                size="small"
                autoFocus
                error={editEmailDialog.newEmail.trim() !== "" && (!isValidEmail(editEmailDialog.newEmail) || !isEmailInOrganization(editEmailDialog.newEmail))}
                helperText={
                  editEmailDialog.newEmail.trim() !== "" && !isValidEmail(editEmailDialog.newEmail)
                    ? "Invalid email"
                    : editEmailDialog.newEmail.trim() !== "" && !isEmailInOrganization(editEmailDialog.newEmail)
                    ? "This email is not present in our organization"
                    : ""
                }
              />
            )}
          />
        </Box>
        <Divider />
        <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={() => setEditEmailDialog({ open: false, oldEmail: "", newEmail: "" })} variant="outlined" size="small">Cancel</Button>
          <Button onClick={handleSaveEditEmail} variant="contained" size="small" disabled={!editEmailDialog.newEmail.trim() || !isValidEmail(editEmailDialog.newEmail) || !isEmailInOrganization(editEmailDialog.newEmail)}>Save</Button>
        </Box>
      </Drawer>
    </Drawer>
  );
}

export type { EditTarget };